import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface PageMetadata {
  title: string;
  subtitle?: string;
  showBackIcon?: boolean; // For navigation icon changes (e.g., Templates -> Back arrow when in inbox view)
}

interface PageActionsContextType {
  registerActions: (path: string, actions: ReactNode) => void;
  getActions: (path: string) => ReactNode | null;
  clearActions: (path: string) => void;
  registerMetadata: (path: string, metadata: PageMetadata) => void;
  getMetadata: (path: string) => PageMetadata | null;
}

const PageActionsContext = createContext<PageActionsContextType | undefined>(undefined);

interface PageActionsProviderProps {
  children: ReactNode;
}

export const PageActionsProvider: React.FC<PageActionsProviderProps> = ({ children }) => {
  const [actionsMap, setActionsMap] = useState<Map<string, ReactNode>>(new Map());
  const [metadataMap, setMetadataMap] = useState<Map<string, PageMetadata>>(new Map());

  const registerActions = useCallback((path: string, actions: ReactNode) => {
    setActionsMap(prev => {
      const newMap = new Map(prev);
      newMap.set(path, actions);
      return newMap;
    });
  }, []);

  const getActions = useCallback((path: string): ReactNode | null => {
    return actionsMap.get(path) || null;
  }, [actionsMap]);

  const clearActions = useCallback((path: string) => {
    setActionsMap(prev => {
      const newMap = new Map(prev);
      newMap.delete(path);
      return newMap;
    });
  }, []);

  const registerMetadata = useCallback((path: string, metadata: PageMetadata) => {
    setMetadataMap(prev => {
      const newMap = new Map(prev);
      newMap.set(path, metadata);
      return newMap;
    });
  }, []);

  const getMetadata = useCallback((path: string): PageMetadata | null => {
    return metadataMap.get(path) || null;
  }, [metadataMap]);

  const value: PageActionsContextType = useMemo(() => ({
    registerActions,
    getActions,
    clearActions,
    registerMetadata,
    getMetadata,
  }), [registerActions, getActions, clearActions, registerMetadata, getMetadata]);

  return (
    <PageActionsContext.Provider value={value}>
      {children}
    </PageActionsContext.Provider>
  );
};

export const usePageActions = (): PageActionsContextType => {
  const context = useContext(PageActionsContext);
  if (context === undefined) {
    throw new Error('usePageActions must be used within a PageActionsProvider');
  }
  return context;
};

